import { View, Text, FlatList, StyleSheet, ActivityIndicator } from 'react-native';
import { useLocalSearchParams, useNavigation } from 'expo-router';
import { useCallback, useEffect } from 'react';
import { useFeedbackHistory } from '@/hooks/useFeedbackHistory';
import { FeedbackHistoryItem } from '@/lib/types';

export default function FeedbackHistoryScreen() {
  const { studentId, name } = useLocalSearchParams<{ studentId: string; name: string }>();
  const navigation = useNavigation();
  const { feedbacks, isLoading } = useFeedbackHistory(studentId ?? '');

  useEffect(() => {
    if (name) navigation.setOptions({ title: name });
  }, [name, navigation]);

  const renderItem = useCallback(({ item }: { item: FeedbackHistoryItem }) => {
    const dateStr = new Date(item.date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
    const timeStr = new Date(item.date).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });

    return (
      <View style={styles.item}>
        <Text style={styles.itemHeader}>
          {item.planName} · {dateStr} às {timeStr}
        </Text>
        <View style={styles.badges}>
          <View style={[styles.badge, item.intensity >= 4 ? styles.badgeHigh : styles.badgeLow]}>
            <Text style={[styles.badgeText, item.intensity >= 4 ? styles.badgeTextHigh : styles.badgeTextLow]}>
              {['😴', '🙂', '💪', '🔥', '🤯'][item.intensity - 1]} Intensidade: {item.intensity}
            </Text>
          </View>
          <View style={[styles.badge, item.fatigue >= 4 ? styles.badgeHigh : styles.badgeLow]}>
            <Text style={[styles.badgeText, item.fatigue >= 4 ? styles.badgeTextHigh : styles.badgeTextLow]}>
              {['😊', '🙂', '😓', '😩', '💀'][item.fatigue - 1]} Cansaço: {item.fatigue}
            </Text>
          </View>
          <View style={[styles.badge, item.hadPain ? styles.badgePain : styles.badgeLow]}>
            <Text style={[styles.badgeText, item.hadPain ? styles.badgeTextPain : styles.badgeTextLow]}>
              {item.hadPain ? `⚠️ ${item.painLocation ?? 'Sim'}` : '✅ sem dor'}
            </Text>
          </View>
        </View>
        {item.observations ? (
          <Text style={styles.observations}>{`"${item.observations}"`}</Text>
        ) : null}
      </View>
    );
  }, []);

  if (isLoading) return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;

  return (
    <FlatList
      style={styles.container}
      contentContainerStyle={styles.content}
      data={feedbacks}
      keyExtractor={(item) => item.id}
      renderItem={renderItem}
      ItemSeparatorComponent={() => <View style={styles.separator} />}
      ListEmptyComponent={
        <Text style={styles.empty}>Nenhum feedback registrado para este aluno.</Text>
      }
    />
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  content: { padding: 16 },
  item: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 14,
  },
  itemHeader: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1A1A1A',
    marginBottom: 8,
  },
  observations: { fontSize: 13, color: '#444', fontStyle: 'italic', marginTop: 10, lineHeight: 18 },
  badges: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  badge: { borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4 },
  badgeText: { fontSize: 11, fontWeight: '600' },
  badgeHigh: { backgroundColor: '#FFF0F2' },
  badgeTextHigh: { color: '#C8102E' },
  badgeLow: { backgroundColor: '#f1f8e9' },
  badgeTextLow: { color: '#388e3c' },
  badgePain: { backgroundColor: '#fce4ec' },
  badgeTextPain: { color: '#c62828' },
  separator: { height: 8 },
  empty: {
    textAlign: 'center',
    color: '#aaa',
    marginTop: 40,
    fontSize: 14,
  },
});
